import React from "react";

import * as Driver from "~/Driver";
import { HoursOfService as GenericHoursOfService } from "~/HoursOfService";
import { Order } from "~/Order";

export function HoursOfService(datum: Driver.Table.Datum) {
  return (
    <>
      {datum.hoursOfService?.type && (
        <GenericHoursOfService.Type type={datum.hoursOfService.type} />
      )}
      {datum.hoursOfService?.exemption && (
        <Driver.Table.Detail>
          <GenericHoursOfService.Exemption
            exemption={datum.hoursOfService.exemption}
          />
        </Driver.Table.Detail>
      )}
    </>
  );
}

export namespace HoursOfService {
  export const column = (filter: Driver.Table.Filter) => ({
    ...Settings.basics(),
    ...Settings.filtering(filter),
    ...Settings.sorting(),
    ...Settings.rendering(),
  });

  namespace Settings {
    export const basics = () => ({
      title: "Hours of Service",
      dataIndex: "hoursOfService",
    });

    export const filtering = (filter: Driver.Table.Filter) => ({
      onFilter: (_: unknown, { hoursOfService }: Driver.Driver) => {
        const f = filter.filter.hoursOfService;

        // Check service type filter
        const serviceTypeFilterEnabled = !!f?.types?.length;
        const serviceTypeFilterMatch =
          serviceTypeFilterEnabled
          && hoursOfService?.type?.id
          && f?.types?.map(t => t.type.id).includes(hoursOfService.type.id)
        // Early exit if filter type enabled, and doesn't match
        if (serviceTypeFilterEnabled && !serviceTypeFilterMatch) return false;

        // Check exemption filter (if both enabled, these filters are AND'ed)
        const exemptionFilterEnabled = !!f?.exemptions?.length;
        const driverHasExemption = !!hoursOfService?.exemption;
        const exemptionFilterMatch =
          exemptionFilterEnabled
          && driverHasExemption
          && f?.exemptions?.map(e => e.exemption.id).includes(hoursOfService?.exemption?.id);
        // Early exit if filter type enabled, and doesn't match
        if (exemptionFilterEnabled && !exemptionFilterMatch) return false;

        return true;
      },
      filteredValue: Filter.isActive(filter) ? ["filtered"] : [],
      filterDropdown: ({
        clearFilters: clear,
        confirm,
      }: {
        readonly confirm: () => void;
        readonly clearFilters?: () => void;
      }) => <Filter {...filter} clear={clear} confirm={confirm} />,
    });

    export const sorting = () => ({
      sorter: (a: Driver.Table.Datum, b: Driver.Table.Datum) =>
        Order.optionally(Driver.Name.compare)(a.name, b.name),
    });

    export const rendering = () => ({
      render: (_: never, datum: Driver.Table.Datum) => (
        <HoursOfService {...datum} />
      ),
    });
  }

  export function Filter({
    filter,
    clear,
    confirm,
  }: Driver.Table.Filter.Props) {
    const controls = {
      confirm,
      ...(Filter.isActive({ filter }) && {
        clear: () => {
          clear?.();
          pipe(
            { filter },
            Driver.Table.Filter.Update.HoursOfService.types(),
            Driver.Table.Filter.Update.HoursOfService.exemptions(),
            filter.update
          );
        },
      }),
    };

    const types = (
      <Driver.Table.Filter.Section>
        <GenericHoursOfService.Types.Select
          placeholder="Filter by type"
          bordered={false}
          style={Driver.Table.Filter.style()}
          selected={filter.hoursOfService}
          select={(types) =>
            filter.update(
              Driver.Table.Filter.Update.HoursOfService.types(types)({ filter })
            )
          }
          clear={() =>
            filter.update(
              Driver.Table.Filter.Update.HoursOfService.types()({ filter })
            )
          }
        />
      </Driver.Table.Filter.Section>
    );

    const exemptions = (
      <Driver.Table.Filter.Section>
        <GenericHoursOfService.Exemptions.Select
          placeholder="Filter by exemption"
          bordered={false}
          style={Driver.Table.Filter.style()}
          selected={filter.hoursOfService}
          select={(exemptions) =>
            filter.update(
              Driver.Table.Filter.Update.HoursOfService.exemptions(exemptions)({
                filter,
              })
            )
          }
          clear={() =>
            filter.update(
              Driver.Table.Filter.Update.HoursOfService.exemptions()({ filter })
            )
          }
        />
      </Driver.Table.Filter.Section>
    );

    return (
      <Driver.Table.Filter filter={filter} {...controls}>
        {types}
        {exemptions}
      </Driver.Table.Filter>
    );
  }

  export namespace Filter {
    export const isActive = ({ filter }: Driver.Table.Filter) =>
      [
        ...(filter.hoursOfService?.types ?? []),
        ...(filter.hoursOfService?.exemptions ?? []),
      ].length > 0;
  }
}
